import { zodResolver } from '@hookform/resolvers/zod';
import { Smartphone } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router';
import { z } from 'zod';

import { Button, Input } from '@/components/ui';
import { paths, REDIRECT_TO_KEY } from '@/config/paths';
import { LoginDetails, useAuth } from '@/features/auth';
import { FormFooter } from '@/features/auth/components/form-footer';
import { FormHeader } from '@/features/auth/components/form-header';
import { useAnalytics } from '@/lib/analytics';
import { noop } from '@/utils';

import { GoogleSignInButton } from '../google-buttons';

const loginSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z
    .string()
    .min(1, 'Password is required')
    .min(8, 'Password must be at least 8 characters'),
});

type LoginFormData = z.infer<typeof loginSchema>;

type LoginFormProps = Readonly<{
  onSubmit?: (data: LoginFormData) => void;
  onOtpSignIn?: () => void;
  isLoading?: boolean;
}>;

export default function LoginForm({
  onSubmit,
  onOtpSignIn,
  isLoading = false,
}: LoginFormProps) {
  const { login, isLoggingIn } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get(REDIRECT_TO_KEY);
  const { trackEvent, clearUserId } = useAnalytics();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const handleFormSubmit = (data: LoginFormData) => {
    // Track login button click
    trackEvent('clickedLoginButton', 1, { method: 'emailPassword' });

    const loginData: LoginDetails = {
      otpLogin: false,
      email: data.email,
      password: data.password,
    };
    login(loginData, {
      onSuccess: () => {
        // Track successful login
        trackEvent('loginApiSuccess', 1, { method: 'emailPassword' });
        const destination = redirectTo ?? paths.app.root.getHref();
        navigate(destination)?.catch(() => noop);
      },
      onError: error => {
        // Track failed login
        trackEvent('loginApiFailure', 1, {
          method: 'emailPassword',
          errorMessage: error?.message ?? 'Unknown error',
        });
        // Remove user id from analytics
        clearUserId();
        navigate(paths.auth.login.getHref())?.catch(() => noop);
      },
    });
    onSubmit?.(data);
  };

  const onForgotPassword = async () => {
    await navigate(paths.auth.forgotPassword.getHref());
  };

  const loading = isLoading || isSubmitting || isLoggingIn;

  return (
    <div className="w-full max-w-md mx-auto p-8 bg-blue-chaos/10 rounded-4xl">
      {/* Header */}
      <FormHeader title="Login" />

      {/* Login Form */}
      <form
        onSubmit={event => {
          event.preventDefault();
          void handleSubmit(handleFormSubmit)(event);
        }}
        className="space-y-6"
      >
        {/* Email Field */}
        <div>
          <Input
            label="Email Address"
            type="email"
            errorText={errors.email?.message}
            {...register('email')}
          />
        </div>

        {/* Password Field */}
        <div>
          <Input
            label="Password"
            type="password"
            errorText={errors.password?.message}
            {...register('password')}
          />
        </div>

        {/* Forgot Password Link */}
        <div className="text-left">
          <Button
            type="button"
            variant="link"
            onClick={() => void onForgotPassword()}
            className="text-sm text-blue-chaos underline hover:text-blue-chaos-dark p-1"
          >
            Forgot Password?
          </Button>
        </div>

        {/* Submit Button */}
        <div className="flex justify-center">
          <Button
            type="submit"
            variant="raised"
            size="lg"
            className="w-56"
            disabled={loading}
          >
            {loading ? 'Logging in...' : 'Login'}
          </Button>
        </div>
      </form>

      {/* Divider */}
      <div className="flex items-center my-6 flex-row">
        <div className="flex-1 border-t border-magical-moonlight-d1 border-dashed"></div>
        <span className="px-4 text-muted-foreground text-sm">or</span>
        <div className="flex-1 border-t border-magical-moonlight-d1 border-dashed"></div>
      </div>

      {/* Alternative Sign-in Options */}
      <div className="space-y-3 flex-col flex">
        <Button
          type="button"
          variant="outline"
          className="w-full"
          icon={<Smartphone size={20} />}
          iconPosition="start"
          onClick={onOtpSignIn}
        >
          Login with OTP
        </Button>

        <GoogleSignInButton />
      </div>

      {/* Footer */}
      <FormFooter />
    </div>
  );
}
